import React from 'react';
import { Input, Label, Select } from '@/src/components/Antd';
import Row from '@/src/components/Layouts/Row/Row';
import { Form } from 'antd';
import { Fragment, useState } from 'react';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import { FPS_BANKCODE, FPS_IDTYPE, FPS_CURRENCY } from '../constant';
import type { Rule } from 'antd/es/form';
import styles from '../css.module.less';

const ReceivingIdInput = ({ selectedIdType }: { selectedIdType: string }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);

  const getLabel = () => {
    if (selectedIdType === FPS_IDTYPE.FPSID)
      return `${getI18n('storePayment.paymentEditPage.FPSID')}*`;
    if (selectedIdType === FPS_IDTYPE.FPSMOBILE)
      return `${getI18n('general.mobile')}*`;
    if (selectedIdType === FPS_IDTYPE.FPSEMAIL)
      return `${getI18n('general.email')}*`;
    return '';
  };

  const getRules = (): Rule[] => {
    if (selectedIdType === FPS_IDTYPE.FPSMOBILE) {
      return [
        {
          validator: (_, value) => {
            if (!value) {
              return Promise.reject(
                new Error(
                  replaceI18nVariables(
                    getI18n('general.fieldisrequired'),
                    getI18n('general.mobile'),
                  ),
                ),
              );
            }
            if (!/^\d{8}$/.test(value)) {
              return Promise.reject(
                new Error(
                  getI18n('storePayment.paymentEditPage.validphonenumber'),
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ];
    } else if (selectedIdType === FPS_IDTYPE.FPSEMAIL) {
      return [
        {
          required: true,
          message: `${replaceI18nVariables(
            getI18n('general.fieldisrequired'),
            getI18n('general.email'),
          )}`,
        },
        {
          type: 'email',
          message: getI18n('storePayment.paymentEditPage.validemail'),
        },
      ];
    }
    return [
      {
        required: true,
        message: `${replaceI18nVariables(
          getI18n('general.fieldisrequired'),
          getI18n('storePayment.paymentEditPage.FPSID'),
        )}`,
      },
    ];
  };

  return (
    <Form.Item labelCol={{ span: 24 }} label={getLabel()}>
      {selectedIdType === FPS_IDTYPE.FPSMOBILE ? (
        <div className={styles.fpsItem}>
          <Input
            size='large'
            style={{ width: '80px' }}
            disabled
            value={'+852'}
          />
          <Form.Item
            name='receivingId'
            noStyle
            rules={getRules()}
            normalize={(value) => value.replace(/\s/g, '')}>
            <Input size='large' style={{ flex: 1, width: '400px' }} />
          </Form.Item>
        </div>
      ) : (
        <Form.Item
          name='receivingId'
          noStyle
          rules={getRules()}
          normalize={(value) => value.replace(/\s/g, '')}>
          <Input size='large' style={{ width: '100%' }} maxLength={100} />
        </Form.Item>
      )}
    </Form.Item>
  );
};

export default ({ form }) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const receivingIdType = form.getFieldValue('receivingIdType');

  const [selectedIdType, setSelectedIdType] = useState<string | null>(
    receivingIdType,
  );

  return (
    <Fragment>
      <Row
        elements={[
          {
            child: (
              <Form.Item
                name='receivingBankCode'
                labelCol={{ span: 24 }}
                label={
                  <Label
                    title={`${getI18n(
                      'storePayment.paymentEditPage.SelectBankorSVF',
                    )}*`}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: replaceI18nVariables(
                      getI18n('general.fieldisrequired'),
                      getI18n('storePayment.paymentEditPage.SelectBankorSVF'),
                    ),
                  },
                ]}>
                <Select
                  size='large'
                  options={Object.entries(FPS_BANKCODE).map(([key, value]) => ({
                    value: key,
                    label: `${getI18n(
                      'storePayment.paymentEditPage.' + value,
                    )}`,
                  }))}
                />
              </Form.Item>
            ),
          },
          { child: '' },
        ]}
      />

      <Row
        elements={[
          {
            child: (
              <Form.Item
                name='receivingIdType'
                labelCol={{
                  span: 24,
                }}
                label={
                  <Label
                    title={`${getI18n(
                      'storePayment.paymentEditPage.Transferto',
                    )}*`}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: replaceI18nVariables(
                      getI18n('general.fieldisrequired'),
                      getI18n('storePayment.paymentEditPage.Transferto'),
                    ),
                  },
                ]}>
                <Select
                  size='large'
                  options={[
                    {
                      value: FPS_IDTYPE.FPSID,
                      label: `${getI18n('storePayment.paymentEditPage.FPSID')}`,
                    },
                    {
                      value: FPS_IDTYPE.FPSMOBILE,
                      label: `${getI18n('general.mobile')}`,
                    },
                    {
                      value: FPS_IDTYPE.FPSEMAIL,
                      label: `${getI18n('general.email')}`,
                    },
                  ]}
                  onChange={(v) => {
                    setSelectedIdType(v);
                    form.setFieldsValue({ receivingId: '' });
                  }}
                />
              </Form.Item>
            ),
          },
          selectedIdType
            ? {
                child: <ReceivingIdInput selectedIdType={selectedIdType} />,
              }
            : { child: '' },
        ]}
      />
      <Row
        elements={[
          {
            child: (
              <Form.Item
                name='receivingCurrency'
                labelCol={{
                  span: 24,
                }}
                label={<Label title={`${getI18n('general.currency')}*`} />}
                rules={[
                  {
                    required: true,
                    message: replaceI18nVariables(
                      getI18n('general.fieldisrequired'),
                      getI18n('general.currency'),
                    ),
                  },
                ]}>
                <Select
                  size='large'
                  options={Object.entries(FPS_CURRENCY).map(([key, value]) => ({
                    value: key,
                    label: value,
                  }))}
                />
              </Form.Item>
            ),
          },
          {
            child: '',
          },
        ]}
      />
    </Fragment>
  );
};
